import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import SponsorDropdown from "../DashboardComponents/SponsorDropdown";
import ProjectDropdown from "../DashboardComponents/ProjectDropdown";

import "../../styles/DashboardStyling/SideBar.css";

// Default values for when both sponsor and project are "All"
const defaultSponsorHeading = "Live Well Collaborative";
const defaultSponsorDescription =
  "This is the Live Well Collaborative Project Explorer.";

const SideBar = ({
  isLoggedIn,
  setIsLoggedIn,
  userData,
  setUserData,
  selectedSponsor,
  selectedProject,
  setSelectedProject,
  setSelectedSponsor,
  onSponsorChange,
}) => {
  const lwcLogo = `${process.env.PUBLIC_URL}/assets/images/Live_Well_Collaborative_Logo.png`;
  const elasticSearch = `${process.env.PUBLIC_URL}/assets/images/ElasticSearch_Logo.png`;

  const formatTimelineData = (timelineArray) => {
    const lines = [];
    for (let i = 0; i < timelineArray.length; i += 2) {
      if (i + 1 < timelineArray.length) {
        lines.push(`${timelineArray[i]} - ${timelineArray[i + 1]}`);
      } else {
        lines.push(timelineArray[i]); // last item alone
      }
    }
    return lines.join("\n");
  };
  

  const [sponsors, setSponsors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const cancelToken = axios.CancelToken.source();
    const fetchData = async () => {
      try {
        const [sponsorRes, projectRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sponsors`, {
            cancelToken: cancelToken.token,
          }),
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
            cancelToken: cancelToken.token,
          }),
        ]);
        if (sponsorRes.data.success && projectRes.data.success) {
          setSponsors(sponsorRes.data.sponsors || []);
          setProjects(projectRes.data.projects || []);
          setLoaded(true);
        } else {
          setError("Failed to load sponsor/project data.");
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          setError("Error fetching data.");
        }
      }
    };

    fetchData();
    return () => cancelToken.cancel("SideBar unmounted");
  }, []);

  // Lookup sponsor and project objects (assumes names are unique and case-insensitive)
  const sponsorObj = sponsors.find(
    (s) => s.spname?.toLowerCase() === selectedSponsor.toLowerCase()
  );
  const projectObj = projects.find(
    (p) => p.pname?.toLowerCase() === selectedProject.toLowerCase()
  );

  // Calculate headings
  let sponsorHeading =
    selectedSponsor === "All"
      ? "Live Well Collaborative"
      : sponsorObj?.spname || selectedSponsor;
  let projectHeading =
    selectedProject === "All" ? "" : projectObj?.pname || selectedProject;

  // Advanced dynamic logic for sidebar information
  let displaySponsorHeading = sponsorHeading;
  let displaySponsorDescription = sponsorObj?.spdescription || "";
  let displayProjectHeading = projectHeading;
  let displayProjectDescription = projectObj?.pdescription || "";
  let displayTimelineLabel = "";
  let displayTimelineData = [];
  let displayPOC = null;
  let displaySPPOC = null;

  // If both are "All" then use default view:
  if (selectedSponsor === "All" && selectedProject === "All") {
    displaySponsorHeading = defaultSponsorHeading;
    displaySponsorDescription = defaultSponsorDescription;
    displayProjectHeading = "";
    displayProjectDescription = "";
    // You can set timeline or POC defaults here if desired
  } else if (selectedSponsor !== "All" && selectedProject === "All") {
    // Only sponsor selected → show sponsor timeline and POC
    displaySponsorHeading = sponsorObj?.spname || selectedSponsor;
    displaySponsorDescription = sponsorObj?.spdescription || "";
    // Show sponsor timeline
    displayTimelineLabel = "Sponsor Timeline";
    displayTimelineData = sponsorObj?.sptimeline || [];
  } else if (selectedSponsor !== "All" && selectedProject !== "All") {
    // Both selected → show project timeline and POC along with sponsor details
    displaySponsorHeading = sponsorObj?.spname || selectedSponsor;
    displaySponsorDescription = "";

    // Show project heading & description
    displayProjectHeading = projectObj?.pname || selectedProject;
    displayProjectDescription = projectObj?.pdescription || "";

    // Show project timeline & POC
    displayTimelineLabel = "Project Duration";
    displayTimelineData = projectObj?.ptimeline || [];
    displayPOC = projectObj?.ppoc;
    displaySPPOC = projectObj?.sppoc;
  } else if (selectedSponsor === "All" && selectedProject !== "All") {
    // This scenario: No sponsor selected but specific project selected.
    // Fallback to project details.
    displayTimelineLabel = "Project Timeline";
    displayTimelineData = projectObj?.ptimeline || [];
    displayPOC = projectObj?.ppoc;
  }

  if (!loaded && !error) {
    return (
      <p className="sidebar-status sidebar-status-loading">
        Loading sponsor/project data...
      </p>
    );
  }
  if (error) {
    return <p className="sidebar-status sidebar-status-error">{error}</p>;
  }

  return (
    <div className="sidebar-container">
      {/* Sidebar header remains unchanged */}
      <div className="sidebar-header">
        <div className="logos-container" style={{cursor: "pointer"}} onClick={() => (window.location.href = 'https://www.livewellcollaborative.org')}>
          <div className="logo-wrapper">
            <img src={lwcLogo} alt="LWC Logo" className="logo-image" />
          </div>
          <div className="logo-wrapper">
            <img
              src={elasticSearch}
              alt="ElasticSearch Logo"
              className="logo-image"
            />
          </div>
        </div>
        <div className="dropdown-container">
          <p className="dropdown-heading">Currently learning about:</p>
          <div className="sponsor-dropdown-container">
            <SponsorDropdown
              userData={userData}
              selectedSponsor={selectedSponsor}
              setSelectedSponsor={setSelectedSponsor}
              onSponsorChange={setSelectedSponsor}
              sponsors={sponsors}
              setSponsors={setSponsors}
            />
          </div>
          <div className="project-dropdown-container">
            <ProjectDropdown
              userData={userData}
              selectedSponsor={selectedSponsor}
              selectedProject={selectedProject}
              setSelectedProject={setSelectedProject}
              projects={projects}
              setProjects={setProjects}
            />
          </div>
        </div>
      </div>
      {/* Advanced Sidebar Information starts below */}
      <div className="sidebar-information">
        {/* SPONSOR INFO */}
        {displaySponsorHeading && (
          <section className="sidebar-section sponsor-heading">
            <h3 className="section-title sponsor-title">Sponsor</h3>
            <p className="section-text sponsor-heading-text">
              {displaySponsorHeading}
            </p>
            {/* TIMELINE (only if non-empty) */}
            {selectedProject === "All" && displayTimelineLabel && displayTimelineData.length > 0 && (
              <section className="sidebar-section timeline-block">
                <h4 className="section-title timeline-title">
                  {displayTimelineLabel}
                </h4>
                <p className="section-text timeline-text" style={{ whiteSpace: "pre-line" }}>
                  {formatTimelineData(displayTimelineData)}
                </p>
              </section>
            )}
            {displaySponsorDescription && (
              <div className="description-block">
                <h4 className="description-heading">Description</h4>
                <p className="description-text">
                  {displaySponsorDescription}
                </p>
              </div>
            )}
          </section>
        )}

        {/* PROJECT INFO (only if it has a heading) */}
        {displayProjectHeading && (
          <section className="sidebar-section project-heading-section">
            <h3 className="section-title project-title">Project</h3>
            <p className="section-text project-heading-text">
              {displayProjectHeading}
            </p>
            {selectedProject !== "All" && displayTimelineLabel && displayTimelineData.length > 0 && (
              <section className="sidebar-section timeline-block">
                <h4 className="section-title timeline-title">
                  {displayTimelineLabel}
                </h4>
                <p className="section-text timeline-text" style={{ whiteSpace: "pre-line" }}>
                  {formatTimelineData(displayTimelineData)}
                </p>
              </section>
            )}
            {displayProjectDescription && (
              <div className="description-block">
                <h4 className="description-heading">Description</h4>
                <p className="description-text">
                  {displayProjectDescription}
                </p>
              </div>
            )}
          </section>
        )}

        {/* POINT OF CONTACT (only if non-null) */}
        {displayPOC && (
          <section className="sidebar-section poc-section">
            <ul className="poc-list">
              <li className="poc-item">
                <strong className="poc-label">
                  {selectedProject === "All" ? "Sponsor Team:" : "Project Team:"}
                </strong>{" "}
                <span className="poc-value">
                  {Array.isArray(displayPOC)
                    ? displayPOC.join(", ")
                    : displayPOC}
                </span>
              </li>
            </ul>
          </section>
        )}

        {displaySPPOC && (
          <section className="sidebar-section poc-section">
            <ul className="poc-list">
              <li className="poc-item">
                <strong className="poc-label">
                  {selectedProject === "All" ? "Sponsor Team:" : "Sponsor Team:"}
                </strong>{" "}
                <span className="poc-value">
                  {Array.isArray(displaySPPOC)
                    ? displaySPPOC.join(", ")
                    : displaySPPOC}
                </span>
              </li>
            </ul>
          </section>
        )}
      </div>
    </div>
  );
};

export default SideBar;
